import { Loader2Icon } from "lucide-react"
import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type Ref,
} from "react"
import { Input, inputVariants } from "@/primitives/input.tsx"
import { cn } from "@/primitives/utils"
import { oneLine, settledDraft } from "./inline-edit.ts"

/*
 * Text that is its own editor: a ticket's title, a Group's name, a Status's name. At rest it reads
 * as text, with a quiet fill on hover; a click or Enter turns it into a field of the same size in
 * the same place, so nothing on the page moves. Enter or leaving the field saves, Esc puts the text
 * back.
 *
 * While the save runs, the new text stands with a spinner after it. A refusal puts the old text
 * back with a brief red rim, and the page says why in `error`, beneath it (a name that is taken, for
 * one). `readOnly` is plain text, for a Member who can't change it.
 *
 * Enter saves, so the on-screen keyboard's Enter key says "done". A `ui` one that may `grow` takes,
 * on `compact`, the width its row leaves it while it is edited, rather than its text's: a phone
 * renames a list row's name with a thumb, and a field as narrow as a short name is hard to work in.
 */

const sizes = {
  display: { text: "text-display font-semibold", Tag: "h1" },
  heading: { text: "text-heading font-semibold", Tag: "h2" },
  ui: { text: "text-ui font-medium", Tag: "span" },
} as const

/** How long the red rim of a refused save shows. */
const flashFor = 600

// Where the browser can't size a textarea to its text, the field grows by measuring it instead.
const sizesItself = typeof CSS !== "undefined" && CSS.supports("field-sizing", "content")

type Mode = "rest" | "editing" | "saving"

/**
 * What a page can ask of an InlineEdit from elsewhere: `edit` turns it into its field, as a menu's
 * "Rename" does, just as a click on the text would; given a `seed`, the field starts from that
 * rather than the text, as a Member's own draft does when someone else's change refused it. Asked
 * while a save is still running, the field opens once the save has settled. `close` ends an edit
 * as Enter (`save`) or Esc would, and says whether there was one to end.
 */
export type InlineEditHandle = {
  edit: (seed?: string) => void
  close: (save: boolean) => boolean
}

export function InlineEdit({
  value,
  onSave,
  size,
  label,
  readOnly = false,
  error,
  grow = false,
  onEdit,
  ref,
}: {
  value: string
  /** Saves the new text. Rejecting says the save was refused, and the text goes back. */
  onSave: (next: string) => Promise<void>
  size: keyof typeof sizes
  /** What the text is, as "Title" or "Group name", naming the field while it is edited. */
  label: string
  readOnly?: boolean
  error?: string
  /** A `ui` field's width on `compact`: the row's, rather than its text's. */
  grow?: boolean
  /**
   * Called as the Member starts an edit (a click, Enter, or `edit`), so a page can drop an `error`
   * about an earlier try once a new one begins.
   */
  onEdit?: () => void
  ref?: Ref<InlineEditHandle>
}) {
  const { text, Tag } = sizes[size]
  const [mode, setMode] = useState<Mode>("rest")
  const [draft, setDraft] = useState(value)
  // What was saved, shown until the page hands back the value it saved to.
  const [saved, setSaved] = useState<{ text: string; over: string }>()
  const [flash, setFlash] = useState(false)
  const rest = useRef<HTMLElement>(null)
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null)
  const refocus = useRef(false)
  const finishing = useRef(false)
  const flashing = useRef<ReturnType<typeof setTimeout>>(undefined)
  // The text the field reopens with once the running save settles, when `edit` asked meanwhile.
  const reopenWith = useRef<string>(undefined)
  const saving = useRef(false)
  useEffect(() => () => clearTimeout(flashing.current), [])

  const shown = saved && saved.over === value ? saved.text : value

  useLayoutEffect(() => {
    if (mode === "editing") {
      const input = field.current
      input?.focus()
      input?.setSelectionRange(input.value.length, input.value.length)
    } else if (refocus.current) {
      refocus.current = false
      rest.current?.focus()
    }
  }, [mode])

  useLayoutEffect(() => {
    const input = field.current
    if (sizesItself || !input || size === "ui") return
    input.style.height = "auto"
    input.style.height = `${input.scrollHeight}px`
  }, [draft, mode, size])

  function start(seed?: string) {
    if (readOnly || mode !== "rest") return
    finishing.current = false
    setDraft(seed ?? shown)
    setMode("editing")
    onEdit?.()
  }

  useImperativeHandle(ref, () => ({
    edit: (seed) => {
      if (readOnly) return
      if (saving.current) reopenWith.current = seed ?? draft
      else start(seed)
    },
    close: (save) => {
      if (mode !== "editing") return false
      void finish(save)
      return true
    },
  }))

  async function finish(save: boolean) {
    if (finishing.current) return
    finishing.current = true
    const next = save ? settledDraft(draft, value) : undefined
    if (next === undefined) return setMode("rest")
    setSaved({ text: next, over: value })
    setMode("saving")
    saving.current = true
    try {
      await onSave(next)
    } catch {
      setSaved(undefined)
      setFlash(true)
      clearTimeout(flashing.current)
      flashing.current = setTimeout(() => setFlash(false), flashFor)
    } finally {
      saving.current = false
      const reopen = reopenWith.current
      reopenWith.current = undefined
      if (reopen === undefined) setMode("rest")
      else {
        finishing.current = false
        setDraft(reopen)
        setMode("editing")
      }
    }
  }

  function onFieldKey(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault()
      event.stopPropagation()
      refocus.current = true
      void finish(true)
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      refocus.current = true
      void finish(false)
    }
  }

  const fieldClass = cn(
    text,
    "rounded-md px-1.5 ring-1 ring-border-strong focus-visible:ring-1 focus-visible:ring-border-strong",
  )

  const editor =
    size === "ui" ? (
      <Input
        ref={field}
        variant="bare"
        aria-label={label}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onFieldKey}
        onBlur={() => void finish(true)}
        enterKeyHint="done"
        // The field's step on touch, as every field's, since `text-ui` is under 16px there.
        className={cn(
          fieldClass,
          "w-auto min-w-16 field-sizing-content touch:text-field",
          grow && "compact:w-full compact:field-sizing-fixed",
        )}
      />
    ) : (
      <textarea
        ref={field}
        rows={1}
        aria-label={label}
        value={draft}
        onChange={(event) => setDraft(oneLine(event.target.value))}
        onKeyDown={onFieldKey}
        onBlur={() => void finish(true)}
        enterKeyHint="done"
        className={cn(
          inputVariants({ variant: "bare" }),
          fieldClass,
          "block resize-none overflow-hidden field-sizing-content",
        )}
      />
    )

  const spinner = mode === "saving" && (
    <Loader2Icon
      role="status"
      aria-label="Saving"
      className="ml-1.5 inline-block size-3.5 shrink-0 animate-spin align-middle text-subtle-foreground"
    />
  )

  return (
    <div
      className={cn(
        "min-w-0",
        size === "ui" ? "inline-flex flex-col" : "block",
        grow && mode === "editing" && "compact:flex-1",
      )}
    >
      <div className={cn("-mx-1.5 min-w-0", size === "ui" && "flex")}>
        {mode === "editing" ? (
          editor
        ) : readOnly ? (
          <Tag className={cn(text, "block px-1.5 wrap-break-word", size === "ui" && "truncate")}>
            {shown}
          </Tag>
        ) : (
          // On touch the name reaches 44px with `touch-target`. A `ui` name is cut short with an
          // ellipsis, which would clip an `::after` of its own, so its hit area is drawn around the
          // Tag, which it can reach past, instead.
          <Tag
            className={cn(
              text,
              size === "ui" ? "flex min-w-0 items-center touch:relative" : "block",
            )}
          >
            <span
              ref={rest}
              role="button"
              tabIndex={0}
              aria-description={`Edit ${label.toLowerCase()}`}
              aria-invalid={error ? true : undefined}
              onClick={() => start()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  start()
                }
              }}
              className={cn(
                "cursor-text rounded-md px-1.5 ring-1 ring-transparent transition duration-100 ease-snappy outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
                "touch-target",
                size === "ui" ? "min-w-0 truncate touch:static" : "block wrap-break-word",
                flash && "ring-destructive focus-visible:ring-destructive",
                mode === "saving" && "cursor-default hover:bg-transparent",
              )}
            >
              {shown}
              {size !== "ui" && spinner}
            </span>
            {size === "ui" && spinner}
          </Tag>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  )
}
